import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseMutationResult } from '@tanstack/react-query';

import { isAmbiguousFailure } from '../../../../api';
import { updateMySettings } from '../../api/settings.api';
import type { SettingsPatch, SettingsSaved } from '../../types/domain.types';
import { ctlKeys, ctlMutationKeys, ctlMutationScopes } from '../../utils/query-keys';
import { myFeedQueryOptions, mySettingsQueryOptions } from '../../utils/queryOptions';

export interface UpdateMySettingsVars {
  readonly patch: SettingsPatch;
}

const movesQuietHours = (patch: SettingsPatch): boolean => patch.quietHours !== undefined || patch.quietHoursEnabled !== undefined;

const movesRouting = (patch: SettingsPatch): boolean =>
  patch.modes !== undefined || patch.handoffAlerts !== undefined || movesQuietHours(patch);

const saveMine = ({ patch }: UpdateMySettingsVars): Promise<SettingsSaved> => updateMySettings(patch);

const refreshWhatDependsOn = (queryClient: QueryClient, patch: SettingsPatch): void => {
  if (movesQuietHours(patch)) void queryClient.invalidateQueries({ queryKey: myFeedQueryOptions().queryKey });
  if (movesRouting(patch)) void queryClient.invalidateQueries({ queryKey: ctlKeys.routingAll() });
};

export const useUpdateMySettings = (): UseMutationResult<SettingsSaved, Error, UpdateMySettingsVars> => {
  const queryClient = useQueryClient();
  const { queryKey } = mySettingsQueryOptions();
  return useMutation({
    mutationKey: ctlMutationKeys.settings(),
    scope: ctlMutationScopes.settingsMe,
    mutationFn: saveMine,
    onMutate: () => queryClient.cancelQueries({ queryKey }),
    onSuccess: async (saved, { patch }) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, saved.settings);
      refreshWhatDependsOn(queryClient, patch);
    },
    onError: (error, { patch }) => {
      if (!isAmbiguousFailure(error)) return;
      void queryClient.invalidateQueries({ queryKey });
      refreshWhatDependsOn(queryClient, patch);
    },
  });
};
